import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Eye, Sparkles, Loader2, ArrowLeft, Send, CheckCircle, XCircle, AlertTriangle, Upload, Maximize2, Minimize2, Clock, FileText, History } from "lucide-react";
import VersionHistory from "@/components/VersionHistory";

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", content: "", featured_image: "",
    categories: [], tags: [], rubrique_id: "", author_id: "", author_ids: [], seo_title: "", meta_description: "",
    canonical_url: "", og_image: "", is_featured: false, status: "draft", locale: "fr", related_article_ids: [],
    scheduled_date: ""
  });
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [rubriques, setRubriques] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [newTag, setNewTag] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const featuredImageRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  // Word count & reading time
  const wordStats = useMemo(() => {
    const text = (form.content || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
    const words = text ? text.split(/\s+/).length : 0;
    const readingTime = Math.max(1, Math.ceil(words / 250));
    const chars = text.length;
    return { words, readingTime, chars };
  }, [form.content]);

  // SEO score
  const seoScore = useMemo(() => {
    let score = 0;
    const checks = [];
    const title = form.seo_title || form.title;
    if (title && title.length >= 30 && title.length <= 65) { score += 20; checks.push({ ok: true, text: "Titre SEO (30-65 car.)" }); }
    else checks.push({ ok: false, text: `Titre SEO: ${title?.length || 0} car. (idéal: 30-65)` });
    if (form.meta_description && form.meta_description.length >= 120 && form.meta_description.length <= 160) { score += 20; checks.push({ ok: true, text: "Meta description (120-160 car.)" }); }
    else checks.push({ ok: false, text: `Meta description: ${(form.meta_description || "").replace(/<[^>]+>/g, "").length} car. (idéal: 120-160)` });
    if (form.featured_image) { score += 20; checks.push({ ok: true, text: "Image mise en avant" }); }
    else checks.push({ ok: false, text: "Image mise en avant manquante" });
    if (wordStats.words >= 300) { score += 20; checks.push({ ok: true, text: `${wordStats.words} mots (min. 300)` }); }
    else checks.push({ ok: false, text: `${wordStats.words} mots (min. 300)` });
    if (form.tags?.length >= 2) { score += 10; checks.push({ ok: true, text: `${form.tags.length} tags` }); }
    else checks.push({ ok: false, text: `${form.tags?.length || 0} tags (min. 2)` });
    if (form.categories?.length >= 1) { score += 10; checks.push({ ok: true, text: "Catégorie assignée" }); }
    else checks.push({ ok: false, text: "Aucune catégorie" });
    return { score, checks };
  }, [form.seo_title, form.title, form.meta_description, form.featured_image, wordStats.words, form.tags, form.categories]);

  // Local draft backup (no server writes — prevents cross-tab overwrites)
  useEffect(() => {
    if (!isEditing) return;
    const saveLocal = () => {
      try {
        localStorage.setItem(`article_draft_${id}`, JSON.stringify(formRef.current));
      } catch { /* storage full */ }
    };
    const timer = setInterval(saveLocal, 30000);
    return () => clearInterval(timer);
  }, [isEditing, id]);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/tags"), api.get("/authors"), api.get("/rubriques")])
      .then(async ([c, t, a, r]) => {
        setCategories(c.data); setTags(t.data); setAuthors(a.data); setRubriques(r.data);
        const artRes = await api.get("/articles?admin=true&status=all&limit=200");
        setAllArticles(artRes.data.articles || []);
        if (isEditing) {
          const article = artRes.data.articles.find(ar => ar.id === id);
          if (article) {
            // Convert UTC scheduled_date to local time for the datetime-local input
            if (article.scheduled_date) {
              const d = new Date(article.scheduled_date);
              if (!isNaN(d.getTime())) {
                const pad = (n) => String(n).padStart(2, "0");
                article.scheduled_date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              }
            }
            setForm(prev => ({ ...prev, ...article }));
          }
        }
      }).catch(console.error);
  }, [id, isEditing]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Convert local datetime-local string to UTC ISO for storage
  const localToUTC = (localStr) => {
    if (!localStr) return localStr;
    const d = new Date(localStr);
    return isNaN(d.getTime()) ? localStr : d.toISOString();
  };

  // Convert UTC ISO string to local datetime-local format for display
  const utcToLocal = (utcStr) => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return utcStr;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleSave = async (status = form.status) => {
    setSaving(true);
    try {
      const payload = { ...form, status };
      // Convert scheduled_date to UTC before sending
      if (payload.scheduled_date) {
        payload.scheduled_date = localToUTC(payload.scheduled_date);
      }
      // Remove server-managed fields to avoid concurrency conflicts
      delete payload.updated_date;
      delete payload.created_at;
      delete payload.created_by;
      delete payload.publication_date;
      delete payload.reading_time;
      delete payload.views;
      delete payload.author;
      delete payload.authors;
      delete payload.category_details;
      delete payload.tag_details;
      delete payload.prev_article;
      delete payload.next_article;
      if (isEditing) { await api.put(`/articles/${id}`, payload); toast.success("Article mis à jour"); }
      else { const res = await api.post("/articles", payload); toast.success("Article créé"); navigate(`/admin/articles/${res.data.id}/edit`); }
    } catch { toast.error("Erreur lors de la sauvegarde"); }
    setSaving(false);
  };

  const aiAssist = async (type) => {
    setAiLoading(type);
    try {
      const res = await api.post("/ai/assist", { content: form.content, title: form.title, prompt_type: type });
      if (type === "excerpt") update("excerpt", res.data.result);
      else if (type === "seo_title") update("seo_title", res.data.result);
      else if (type === "meta_description") update("meta_description", res.data.result);
      else if (type === "suggest_tags") {
        // Parse comma-separated tags and create them
        const tagNames = res.data.result.split(",").map(t => t.trim()).filter(Boolean);
        let addedCount = 0;
        for (const name of tagNames) {
          const existing = tags.find(t => t.name.toLowerCase() === name.toLowerCase());
          if (existing) {
            if (!form.tags.includes(existing.id)) {
              update("tags", [...form.tags, existing.id]);
            }
            addedCount++;
          } else {
            try {
              const tagRes = await api.post("/tags", { name });
              const created = tagRes.data;
              setTags(prev => [...prev, created]);
              update("tags", [...form.tags, created.id]);
              addedCount++;
            } catch { /* tag create error */ }
          }
        }
        toast.success(`${addedCount} tags suggérés et appliqués`);
      } else {
        toast.info(res.data.result);
      }
      if (type !== "suggest_tags") toast.success("Suggestion IA générée");
    } catch (err) { toast.error("Erreur IA : " + (err.response?.data?.detail || "service indisponible")); }
    setAiLoading("");
  };

  const autoFillAll = async () => {
    if (!form.title && !form.content) { toast.error("Ajoutez un titre et du contenu d'abord"); return; }
    setAiLoading("autofill");
    const updates = {};
    try {
      // 1. Slug from title
      if (!form.slug && form.title) {
        updates.slug = form.title.toLowerCase()
          .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a').replace(/[ùûü]/g, 'u')
          .replace(/[ôö]/g, 'o').replace(/[îï]/g, 'i').replace(/[ç]/g, 'c').replace(/[ñ]/g, 'n')
          .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 80);
      }

      // 2. Canonical URL from slug
      const slug = updates.slug || form.slug;
      if (slug) updates.canonical_url = `https://blog.ofalycee.org/article/${slug}`;

      // 3. Extract first image from content as featured_image
      if (!form.featured_image && form.content) {
        const imgMatch = form.content.match(/<img[^>]+src="([^"]+)"/);
        if (imgMatch) updates.featured_image = imgMatch[1];
      }

      // 4. OG image = featured image
      if (updates.featured_image || form.featured_image) {
        updates.og_image = updates.featured_image || form.featured_image;
      }

      // 5. AI-generated excerpt, SEO title, meta description
      const aiCalls = [];
      if (!form.excerpt) aiCalls.push(api.post("/ai/assist", { content: form.content, title: form.title, prompt_type: "excerpt" }).then(r => { updates.excerpt = r.data.result; }));
      if (!form.seo_title) aiCalls.push(api.post("/ai/assist", { content: form.content, title: form.title, prompt_type: "seo_title" }).then(r => { updates.seo_title = r.data.result; }));
      if (!form.meta_description) aiCalls.push(api.post("/ai/assist", { content: form.content, title: form.title, prompt_type: "meta_description" }).then(r => { updates.meta_description = r.data.result; }));

      await Promise.all(aiCalls);

      // Apply all updates
      setForm(prev => ({ ...prev, ...updates }));
      const filled = Object.keys(updates).length;
      toast.success(`${filled} champs remplis automatiquement`);
    } catch (err) {
      // Apply what we have even if AI fails
      if (Object.keys(updates).length > 0) setForm(prev => ({ ...prev, ...updates }));
      toast.error("Certains champs IA n'ont pas pu être générés");
    }
    setAiLoading("");
  };

  const toggleCategory = (catId) => update("categories", form.categories.includes(catId) ? form.categories.filter(c => c !== catId) : [...form.categories, catId]);
  const toggleTag = (tagId) => update("tags", form.tags.includes(tagId) ? form.tags.filter(t => t !== tagId) : [...form.tags, tagId]);

  const addCustomTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    try {
      const res = await api.post("/tags", { name });
      const created = res.data;
      setTags(prev => [...prev, created]);
      update("tags", [...form.tags, created.id]);
      setNewTag("");
      toast.success(`Tag "${name}" créé`);
    } catch { toast.error("Erreur création du tag"); }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = res.data.url || res.data.path;
      update("featured_image", imageUrl);
      toast.success("Image importée");
    } catch {
      toast.error("Erreur lors de l'import de l'image");
    }
    setUploadingImage(false);
    e.target.value = "";
  };

  const handleReview = async (action) => {
    if (!isEditing) return;
    setSaving(true);
    try {
      await api.put(`/articles/${id}/review`, { action, notes: reviewNotes });
      const label = { submit_review: "soumis pour révision", approve: "approuvé", reject: "rejeté", request_changes: "modifications demandées" };
      toast.success(`Article ${label[action] || action}`);
      // Reload article data
      const artRes = await api.get("/articles?admin=true&status=all&limit=200");
      const article = artRes.data.articles.find(ar => ar.id === id);
      if (article) setForm(prev => ({ ...prev, ...article }));
      setReviewNotes("");
    } catch { toast.error("Erreur lors de l'action"); }
    setSaving(false);
  };

  const reviewStatusLabels = {
    in_review: { label: "En révision", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    approved: { label: "Approuvé", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    rejected: { label: "Rejeté", cls: "bg-rose-100 text-rose-800 border-rose-200" },
    changes_requested: { label: "Modifications demandées", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };

  return (
    <div data-testid="article-editor" className={fullscreen ? "fixed inset-0 z-50 bg-background overflow-auto p-6" : ""}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => fullscreen ? setFullscreen(false) : navigate("/admin/articles")} data-testid="back-to-list"><ArrowLeft className="h-4 w-4 mr-2" /> {fullscreen ? "Quitter" : "Retour"}</Button>
          <h1 className="font-heading text-2xl">{isEditing ? "Modifier l'article" : "Nouvel article"}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setFullscreen(!fullscreen)} title={fullscreen ? "Quitter le plein écran" : "Plein écran"} data-testid="fullscreen-btn">
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="outline" className="rounded-none" onClick={autoFillAll} disabled={!!aiLoading} data-testid="autofill-btn">
            {aiLoading === "autofill" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Auto-remplir
          </Button>
          <Button variant="outline" className="rounded-none" onClick={() => handleSave("draft")} disabled={saving} data-testid="save-draft-btn"><Save className="h-4 w-4 mr-2" /> Brouillon</Button>
          {form.status === "scheduled" ? (
            <Button className="rounded-none" onClick={() => handleSave("scheduled")} disabled={saving} data-testid="publish-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Clock className="h-4 w-4 mr-2" />} Programmer
            </Button>
          ) : (
            <Button className="rounded-none" onClick={() => handleSave("published")} disabled={saving} data-testid="publish-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />} Publier
            </Button>
          )}
        </div>
      </div>
      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground border-b pb-2">
        <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {wordStats.words} mots</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> ~{wordStats.readingTime} min de lecture</span>
        <span>{wordStats.chars} caractères</span>
        <span className={`ml-auto flex items-center gap-1 font-medium ${seoScore.score >= 80 ? "text-emerald-600" : seoScore.score >= 50 ? "text-amber-600" : "text-rose-600"}`}>
          SEO {seoScore.score}%
        </span>
        {lastAutoSave && <span className="text-muted-foreground/60">Brouillon local sauvé</span>}
      </div>
      <Tabs defaultValue="content" className="space-y-6">
        <TabsList className="rounded-none">
          <TabsTrigger value="content" className="rounded-none" data-testid="tab-content">Contenu</TabsTrigger>
          <TabsTrigger value="seo" className="rounded-none" data-testid="tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none" data-testid="tab-settings">Paramètres</TabsTrigger>
          {isEditing && <TabsTrigger value="history" className="rounded-none" data-testid="tab-history"><History className="h-3.5 w-3.5 mr-1.5" />Historique</TabsTrigger>}
        </TabsList>
        <TabsContent value="content" className="space-y-6">
          <div><Label>Titre</Label><Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Titre de l'article" className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 mt-2 text-xl font-heading" data-testid="article-title-input" /></div>
          <div><Label>Slug URL</Label><Input value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="slug-auto-généré-si-vide" className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 mt-2 font-mono text-sm" data-testid="article-slug-input" /></div>
          <div>
            <div className="flex items-center justify-between"><Label>Extrait</Label>
              <Button variant="ghost" size="sm" onClick={() => aiAssist("excerpt")} disabled={!!aiLoading} className="text-xs" data-testid="ai-excerpt-btn">
                {aiLoading === "excerpt" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />} Générer avec IA
              </Button>
            </div>
            <RichTextEditor mini value={form.excerpt} onChange={(v) => update("excerpt", v)} />
          </div>
          <div><Label>Contenu</Label><div className="mt-2"><RichTextEditor value={form.content} onChange={(v) => update("content", v)} /></div></div>
          <div>
            <Label>Image mise en avant</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input value={form.featured_image} onChange={(e) => update("featured_image", e.target.value)} placeholder="URL de l'image ou importer..." className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 flex-1" data-testid="article-image-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => featuredImageRef.current?.click()} disabled={uploadingImage} data-testid="upload-featured-image-btn">
                {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                {uploadingImage ? "Import..." : "Importer"}
              </Button>
              <input ref={featuredImageRef} type="file" accept="image/*" className="hidden" onChange={handleFeaturedImageUpload} />
            </div>
            {form.featured_image && <img src={form.featured_image} alt="Aperçu" className="mt-4 max-h-48 object-cover rounded" />}
          </div>
        </TabsContent>
        <TabsContent value="seo" className="space-y-6">
          <div>
            <div className="flex items-center justify-between"><Label>Titre SEO</Label>
              <Button variant="ghost" size="sm" onClick={() => aiAssist("seo_title")} disabled={!!aiLoading} className="text-xs" data-testid="ai-seo-title-btn">
                {aiLoading === "seo_title" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />} IA
              </Button>
            </div>
            <Input value={form.seo_title} onChange={(e) => update("seo_title", e.target.value)} placeholder="Titre SEO (max 60 car.)" className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 mt-2" data-testid="seo-title-input" />
            <p className="text-xs text-muted-foreground mt-1">{(form.seo_title || "").length}/60</p>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Meta Description</Label>
              <Button variant="ghost" size="sm" onClick={() => aiAssist("meta_description")} disabled={!!aiLoading} className="text-xs" data-testid="ai-meta-desc-btn">
                {aiLoading === "meta_description" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />} IA
              </Button>
            </div>
            <Textarea value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} placeholder="Description SEO (max 155 car.)" className="border border-input bg-transparent rounded-none focus-visible:ring-0 mt-2" data-testid="meta-desc-input" />
            <p className="text-xs text-muted-foreground mt-1">{(form.meta_description || "").length}/155</p>
          </div>
          <div><Label>URL Canonique</Label><Input value={form.canonical_url} onChange={(e) => update("canonical_url", e.target.value)} placeholder="https://..." className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 mt-2" data-testid="canonical-url-input" /></div>
          <div><Label>Image OG</Label><Input value={form.og_image} onChange={(e) => update("og_image", e.target.value)} placeholder="URL image réseaux sociaux" className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 mt-2" data-testid="og-image-input" /></div>
          <Separator />
          <div>
            <h3 className="font-heading text-lg mb-3">Score SEO</h3>
            <div className={`text-2xl font-bold mb-3 ${seoScore.score >= 80 ? "text-emerald-600" : seoScore.score >= 50 ? "text-amber-600" : "text-rose-600"}`}>{seoScore.score}%</div>
            <div className="space-y-1.5">
              {seoScore.checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {c.ok ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                  <span className={c.ok ? "text-muted-foreground" : ""}>{c.text}</span>
                </div>
              ))}
            </div>
          </div>
          <Separator />
          <div>
            <h3 className="font-heading text-lg mb-4">Assistance IA</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => aiAssist("improve")} disabled={!!aiLoading} data-testid="ai-improve-btn"><Sparkles className="h-3 w-3 mr-1" /> Suggestions</Button>
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => aiAssist("suggest_tags")} disabled={!!aiLoading} data-testid="ai-tags-btn"><Sparkles className="h-3 w-3 mr-1" /> Suggérer des tags</Button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="settings" className="space-y-6">
          <div><Label>Auteur(s)</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-2">
              {(form.author_ids || []).map((aid) => {
                const a = authors.find(x => x.id === aid);
                return a ? (
                  <span key={aid} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                    {a.name}
                    <button type="button" onClick={() => update("author_ids", form.author_ids.filter(x => x !== aid))} className="hover:text-destructive">&times;</button>
                  </span>
                ) : null;
              })}
            </div>
            <Select value="" onValueChange={(v) => { if (v && !(form.author_ids || []).includes(v)) update("author_ids", [...(form.author_ids || []), v]); }}>
              <SelectTrigger className="rounded-none" data-testid="author-select"><SelectValue placeholder="Ajouter un auteur" /></SelectTrigger>
              <SelectContent>{authors.filter(a => !(form.author_ids || []).includes(a.id)).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Rubrique</Label>
            <Select value={form.rubrique_id} onValueChange={(v) => update("rubrique_id", v)}>
              <SelectTrigger className="rounded-none mt-2" data-testid="rubrique-select"><SelectValue placeholder="Sélectionner une rubrique" /></SelectTrigger>
              <SelectContent>{rubriques.filter(r => !r.external_link).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Catégories</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="category-selector">
              {categories.map((cat) => (<Badge key={cat.id} variant={form.categories.includes(cat.id) ? "default" : "outline"} className="cursor-pointer rounded-none" onClick={() => toggleCategory(cat.id)}>{cat.name}</Badge>))}
            </div>
          </div>
          <div><Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="tag-selector">
              {tags.map((tag) => (<Badge key={tag.id} variant={form.tags.includes(tag.id) ? "default" : "outline"} className="cursor-pointer rounded-none font-mono text-xs" onClick={() => toggleTag(tag.id)}>{tag.name}</Badge>))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nouveau tag..." className="border-b border-input bg-transparent px-0 rounded-none focus-visible:ring-0 max-w-[200px] text-sm" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomTag())} data-testid="new-tag-input" />
              <Button type="button" variant="outline" size="sm" onClick={addCustomTag} disabled={!newTag.trim()} data-testid="add-tag-btn">Ajouter</Button>
            </div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={form.is_featured} onCheckedChange={(v) => update("is_featured", v)} data-testid="featured-switch" /><Label>Article mis en avant</Label></div>
          <div><Label>Statut</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="rounded-none mt-2" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="draft">Brouillon</SelectItem><SelectItem value="published">Publié</SelectItem><SelectItem value="scheduled">Programmé</SelectItem></SelectContent>
            </Select>
          </div>
          {form.status === "scheduled" && (
            <div><Label>Date de publication programmée</Label>
              <Input type="datetime-local" value={form.scheduled_date || ""} onChange={(e) => update("scheduled_date", e.target.value)} className="rounded-none mt-2" data-testid="scheduled-date-input" />
              <p className="text-xs text-muted-foreground mt-1">Heure locale — l&apos;article sera publié automatiquement à cette date</p>
            </div>
          )}
          <div><Label>Langue</Label>
            <Select value={form.locale} onValueChange={(v) => update("locale", v)}>
              <SelectTrigger className="rounded-none mt-2" data-testid="locale-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
            </Select>
          </div>
          {/* Related articles */}
          <div><Label>Articles liés</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-2">
              {(form.related_article_ids || []).map((rid) => {
                const ra = allArticles.find(x => x.id === rid);
                return ra ? (
                  <span key={rid} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                    {ra.title?.substring(0, 40)}{ra.title?.length > 40 ? "..." : ""}
                    <button type="button" onClick={() => update("related_article_ids", form.related_article_ids.filter(x => x !== rid))} className="hover:text-destructive">&times;</button>
                  </span>
                ) : null;
              })}
            </div>
            <Select value="" onValueChange={(v) => { if (v && !(form.related_article_ids || []).includes(v)) update("related_article_ids", [...(form.related_article_ids || []), v]); }}>
              <SelectTrigger className="rounded-none" data-testid="related-articles-select"><SelectValue placeholder="Ajouter un article lié" /></SelectTrigger>
              <SelectContent>{allArticles.filter(a => a.id !== id && !(form.related_article_ids || []).includes(a.id)).slice(0, 30).map((a) => <SelectItem key={a.id} value={a.id}>{a.title?.substring(0, 60)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {/* Workflow Section */}
          {isEditing && (
            <>
              <Separator />
              <div>
                <h3 className="font-heading text-lg mb-3">Workflow de validation</h3>
                {form.review_status && (
                  <div className="mb-3">
                    <span className={`text-xs px-2 py-1 rounded-sm border ${reviewStatusLabels[form.review_status]?.cls || "bg-muted"}`}>
                      {reviewStatusLabels[form.review_status]?.label || form.review_status}
                    </span>
                    {form.review_notes && (
                      <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded-sm">
                        <strong>Notes :</strong> {form.review_notes}
                      </p>
                    )}
                  </div>
                )}
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Notes de révision (optionnel)"
                  className="border border-input bg-transparent rounded-none focus-visible:ring-0 mb-3 min-h-[60px]"
                  data-testid="review-notes-input"
                />
                <div className="flex flex-wrap gap-2">
                  {(!form.review_status || form.review_status === "changes_requested" || form.review_status === "rejected") && (
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => handleReview("submit_review")} disabled={saving} data-testid="submit-review-btn">
                      <Send className="h-3 w-3 mr-1" /> Soumettre pour révision
                    </Button>
                  )}
                  {form.review_status === "in_review" && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => handleReview("approve")} disabled={saving} data-testid="approve-btn">
                        <CheckCircle className="h-3 w-3 mr-1" /> Approuver
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => handleReview("request_changes")} disabled={saving} data-testid="request-changes-btn">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Demander des modifications
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-none text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => handleReview("reject")} disabled={saving} data-testid="reject-btn">
                        <XCircle className="h-3 w-3 mr-1" /> Rejeter
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </TabsContent>
        {isEditing && (
          <TabsContent value="history">
            <VersionHistory articleId={id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
